import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Ticket,
  User,
  Mail,
  Calendar,
  Clock,
  MessageSquare,
  Save,
  Bug,
  HelpCircle,
  Lightbulb,
  Wrench,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SupportTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [formData, setFormData] = useState({
    status: '',
    priority: '',
    category: '',
    assigned_to: '',
    internal_notes: ''
  });

  useEffect(() => {
    fetchTicket();
    fetchAdmins();
  }, [ticketId]);

  const fetchTicket = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support-tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch ticket');
      const data = await response.json();
      setTicket(data);
      setFormData({
        status: data.status,
        priority: data.priority,
        category: data.category,
        assigned_to: data.assigned_to || '',
        internal_notes: data.internal_notes || ''
      });
    } catch (error) {
      toast.error('Failed to load ticket');
      navigate('/super-admin/support');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support-tickets/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdmins(data.admins);
      }
    } catch (error) {
      console.error('Failed to fetch admins:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support-tickets/${ticketId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to update ticket');
      
      const updated = await response.json();
      setTicket(updated);
      toast.success('Ticket updated successfully');
    } catch (error) {
      toast.error('Failed to update ticket');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'bug': return <Bug className="w-5 h-5" />;
      case 'system_improvement': return <Lightbulb className="w-5 h-5" />;
      case 'development_suggestion': return <Wrench className="w-5 h-5" />;
      default: return <HelpCircle className="w-5 h-5" />;
    }
  };

  const getCategoryStyles = (category) => {
    const styles = {
      bug: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'text-red-500' },
      more_help_needed: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: 'text-orange-500' },
      system_improvement: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'text-blue-500' },
      development_suggestion: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', icon: 'text-purple-500' }
    };
    return styles[category] || styles.more_help_needed;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-red-500 text-white',
      medium: 'bg-yellow-500 text-white',
      low: 'bg-green-500 text-white'
    };
    return (
      <Badge className={styles[priority] || styles.medium}>
        {priority?.toUpperCase()}
      </Badge>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="w-4 h-4" />;
      case 'in_progress': return <Clock className="w-4 h-4" />;
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      case 'closed': return <XCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // dd/MM/yyyy HH:mm in NZ time
    return new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  };

  const categoryLabels = {
    bug: 'Bug Report',
    more_help_needed: 'Help Needed',
    system_improvement: 'System Improvement',
    development_suggestion: 'Feature Request'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <Ticket className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-600">Ticket not found</h2>
        <Button onClick={() => navigate('/super-admin/support')} className="mt-4">
          Back to Tickets
        </Button>
      </div>
    );
  }

  const categoryStyle = getCategoryStyles(ticket.category);

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/super-admin/support')}
            className="flex items-center gap-2"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 font-['Manrope'] flex items-center gap-2">
              <Ticket className="w-6 h-6 text-indigo-600" />
              Ticket #{ticket.id.slice(0, 8).toUpperCase()}
            </h1>
            <p className="text-gray-500 text-sm">
              Created {formatDate(ticket.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getPriorityBadge(ticket.priority)}
          <Badge className={`${categoryStyle.bg} ${categoryStyle.text} ${categoryStyle.border} flex items-center gap-1`}>
            {getCategoryIcon(ticket.category)}
            {categoryLabels[ticket.category]}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              Customer Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="font-medium text-gray-900">{ticket.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <a href={`mailto:${ticket.email}`} className="font-medium text-indigo-600 hover:underline">
                    {ticket.email}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <MessageSquare className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Source</p>
                  <p className="font-medium text-gray-900 capitalize">{ticket.source}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Last Updated</p>
                  <p className="font-medium text-gray-900">{formatDate(ticket.updated_at)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Message Card */}
          <div className={`rounded-xl border-2 ${categoryStyle.border} shadow-sm overflow-hidden`}>
            <div className={`${categoryStyle.bg} px-6 py-4 border-b ${categoryStyle.border}`}>
              <h2 className={`text-lg font-semibold ${categoryStyle.text} flex items-center gap-2`}>
                <div className={categoryStyle.icon}>{getCategoryIcon(ticket.category)}</div>
                Customer Message
              </h2>
            </div>
            <div className="p-6 bg-white">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{ticket.message}</p>
            </div>
          </div>

          {/* Chat Transcript */}
          {ticket.chat_transcript && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setShowTranscript(!showTranscript)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  Chat Transcript
                </h2>
                {showTranscript ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              {showTranscript && (
                <div className="px-6 pb-6 border-t border-gray-100">
                  <div className="mt-4 bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                      {ticket.chat_transcript}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar - Management Panel */}
        <div className="space-y-6">
          {/* Status & Assignment */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Ticket Management</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="ticket-status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, status: val }))}
                >
                  <SelectTrigger id="ticket-status" name="status" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">
                      <span className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-500" /> Open
                      </span>
                    </SelectItem>
                    <SelectItem value="in_progress">
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-yellow-500" /> In Progress
                      </span>
                    </SelectItem>
                    <SelectItem value="resolved">
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" /> Resolved
                      </span>
                    </SelectItem>
                    <SelectItem value="closed">
                      <span className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-gray-500" /> Closed
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="ticket-priority" className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <Select
                  value={formData.priority}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}
                >
                  <SelectTrigger id="ticket-priority" name="priority" data-testid="priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span> Low
                      </span>
                    </SelectItem>
                    <SelectItem value="medium">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-500"></span> Medium
                      </span>
                    </SelectItem>
                    <SelectItem value="high">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span> High
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="ticket-category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <Select
                  value={formData.category}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, category: val }))}
                >
                  <SelectTrigger id="ticket-category" name="category" data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">
                      <span className="flex items-center gap-2">
                        <Bug className="w-4 h-4 text-red-500" /> Bug
                      </span>
                    </SelectItem>
                    <SelectItem value="more_help_needed">
                      <span className="flex items-center gap-2">
                        <HelpCircle className="w-4 h-4 text-orange-500" /> Help Needed
                      </span>
                    </SelectItem>
                    <SelectItem value="system_improvement">
                      <span className="flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-blue-500" /> Improvement
                      </span>
                    </SelectItem>
                    <SelectItem value="development_suggestion">
                      <span className="flex items-center gap-2">
                        <Wrench className="w-4 h-4 text-purple-500" /> Feature Request
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="ticket-assign" className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                <Select
                  value={formData.assigned_to || "unassigned"}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, assigned_to: val === "unassigned" ? "" : val }))}
                >
                  <SelectTrigger id="ticket-assign" name="assigned_to" data-testid="assign-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {admins.map(admin => (
                      <SelectItem key={admin.id} value={admin.id}>
                        {admin.name} ({admin.role.replace('_', ' ')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Internal Notes */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-indigo-600" />
              Internal Notes
            </h2>
            <p className="text-xs text-gray-500 mb-2">
              Only visible to admins. Not shared with the customer.
            </p>
            <Textarea
              value={formData.internal_notes}
              onChange={(e) => setFormData(prev => ({ ...prev, internal_notes: e.target.value }))}
              placeholder="Add internal notes about this ticket..."
              rows={6}
              className="resize-none"
              data-testid="internal-notes"
            />
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-indigo-600 hover:bg-indigo-700 flex items-center justify-center gap-2"
            data-testid="save-ticket"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetail;
